import type { TargetMelody } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import { buildScoreOverlay } from './scoreOverlayGeometry';

import type { OverlayPoint } from './scoreOverlayGeometry';

/**
 * ScoreOverlay(#6/#8) — 목표 멜로디(앵커 색 굵은 칸)를 사용자가 실제로 부른 음높이
 * 곡선(primary) 위에 겹쳐 비교한다. `playheadMs`를 주면 라이브 뷰(#8)로서 현재
 * 시각 플레이헤드(수직선)를 함께 그린다(생략 시 정지 후 리뷰). 좌표는 모두 순수
 * `buildScoreOverlay`가 계산하고 여기선 수기 SVG로 그리기만 한다(차트 의존성 없음).
 */

const LEGEND = [
  { color: 'hsl(var(--primary))', label: '실제' },
  { color: 'hsl(var(--ring))', label: '목표' },
] as const;

export const ScoreOverlay = ({
  melody,
  actual,
  playheadMs,
}: {
  melody: TargetMelody;
  actual: OverlayPoint[];
  /** 라이브(#8) 현재 시각. 주면 플레이헤드 수직선을 그린다. */
  playheadMs?: number;
}) => {
  const overlay = buildScoreOverlay({ melody, actual, playheadMs });
  const { width, height, targetLines, actualSegments, playheadX } = overlay;
  const live = playheadX !== null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
          {live ? '실시간 목표 vs 실제' : '목표 vs 실제'}
          <InfoTip label="목표 vs 실제 설명">
            {live ? '지금까지' : '정지 시점까지'} 부른 실제 음높이(곡선) 위에
            목표 멜로디(칸)를 겹친 것입니다. 칸과 곡선이 가까울수록 정확합니다.
            {live ? ' 세로선은 현재 시각(플레이헤드)입니다.' : ''} 표시는 raw f0
            기반이며 점수와는 별개의 비교용 시각화입니다.
          </InfoTip>
        </span>
        <span className="flex items-center gap-3">
          {LEGEND.map((l) => (
            <span
              key={l.label}
              className="flex items-center gap-1 text-[11px] text-muted-foreground"
            >
              <span
                aria-hidden
                className="inline-block h-0.5 w-3 rounded-full"
                style={{ backgroundColor: l.color }}
              />
              {l.label}
            </span>
          ))}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-40 w-full rounded-md border border-border bg-muted/20"
        role="img"
        aria-label="목표 멜로디와 실제 음높이 비교"
      >
        {targetLines.map((line, i) => (
          <line
            key={`t-${i}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            style={{ stroke: 'hsl(var(--ring))' }}
            strokeWidth={4}
            strokeLinecap="round"
            opacity={0.5}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {actualSegments.map((seg, i) =>
          seg.points.includes(' ') ? (
            <polyline
              key={`a-${i}`}
              points={seg.points}
              fill="none"
              style={{ stroke: 'hsl(var(--primary))' }}
              strokeWidth={1.5}
              strokeLinejoin="round"
              opacity={seg.opacity}
              vectorEffect="non-scaling-stroke"
            />
          ) : (
            <circle
              key={`a-${i}`}
              cx={Number(seg.points.split(',')[0])}
              cy={Number(seg.points.split(',')[1])}
              r={1.5}
              style={{ fill: 'hsl(var(--primary))' }}
              opacity={seg.opacity}
            />
          ),
        )}

        {playheadX !== null && (
          <line
            x1={playheadX}
            y1={0}
            x2={playheadX}
            y2={height}
            style={{ stroke: 'hsl(var(--foreground))' }}
            strokeWidth={1}
            strokeDasharray="4 3"
            opacity={0.7}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
    </div>
  );
};
